import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import GestorLayout from '../../components/GestorLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '../../components/ui/dialog';
import { 
  BookOpen, 
  Plus, 
  Edit2, 
  Trash2, 
  Check, 
  X, 
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Save,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  GripVertical
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Ícones por categoria
const CATEGORIA_ICONS = {
  'Ordens de Fabrico (OFs)': '📦',
  'Lotes': '📋',
  'Operadores': '👷',
  'Pausas': '⏸️',
  'Contagem': '🔢',
  'Produção': '⚙️',
  'Anomalias': '⚠️',
  'Tarefas Pontuais': '📌',
  'Horários': '🕐',
  'Eficiência': '📊',
  'Geral': '📖'
};

export default function Regras({ embedded = false }) {
  const [regras, setRegras] = useState([]);
  const [regrasPorCategoria, setRegrasPorCategoria] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoriaAberta, setCategoriaAberta] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingRegra, setEditingRegra] = useState(null);
  const [formData, setFormData] = useState({
    categoria: '',
    titulo: '',
    descricao: '',
    pode_fazer: [],
    nao_pode_fazer: [],
    ordem: 0
  });
  const [novoPodeFazer, setNovoPodeFazer] = useState('');
  const [novoNaoPodeFazer, setNovoNaoPodeFazer] = useState('');

  useEffect(() => {
    loadRegras();
  }, []);

  const loadRegras = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/regras`);
      setRegras(response.data.regras || []);
      setRegrasPorCategoria(response.data.por_categoria || {});
      
      // Abrir primeira categoria por padrão
      if (Object.keys(response.data.por_categoria || {}).length > 0) {
        setCategoriaAberta(Object.keys(response.data.por_categoria)[0]);
      }
    } catch (error) {
      console.error('Erro ao carregar regras:', error);
      toast.error('Erro ao carregar regras');
    } finally {
      setLoading(false);
    }
  };

  const handleResetRegras = async () => {
    if (!window.confirm('Tem a certeza que quer reiniciar todas as regras para os valores padrão? Esta ação não pode ser desfeita.')) {
      return;
    }
    
    try {
      await axios.post(`${API}/regras/reset`);
      toast.success('Regras reiniciadas!');
      await loadRegras();
    } catch (error) {
      toast.error('Erro ao reiniciar regras');
    }
  };

  const openEditDialog = (regra = null) => {
    if (regra) {
      setEditingRegra(regra);
      setFormData({
        categoria: regra.categoria || '',
        titulo: regra.titulo || '',
        descricao: regra.descricao || '',
        pode_fazer: regra.pode_fazer || [],
        nao_pode_fazer: regra.nao_pode_fazer || [],
        ordem: regra.ordem || 0
      });
    } else {
      setEditingRegra(null);
      setFormData({
        categoria: '',
        titulo: '',
        descricao: '',
        pode_fazer: [],
        nao_pode_fazer: [],
        ordem: regras.length + 1
      });
    }
    setNovoPodeFazer('');
    setNovoNaoPodeFazer('');
    setShowEditDialog(true);
  };

  const handleSave = async () => {
    if (!formData.categoria || !formData.titulo) {
      toast.error('Preencha categoria e título');
      return;
    }

    try {
      if (editingRegra) {
        await axios.put(`${API}/regras/${editingRegra.id}`, formData);
        toast.success('Regra atualizada!');
      } else {
        await axios.post(`${API}/regras`, formData);
        toast.success('Regra criada!');
      }
      setShowEditDialog(false);
      await loadRegras();
    } catch (error) {
      toast.error('Erro ao guardar regra');
    }
  };

  const handleDelete = async (regraId) => {
    if (!window.confirm('Tem a certeza que quer eliminar esta regra?')) {
      return;
    }

    try {
      await axios.delete(`${API}/regras/${regraId}`);
      toast.success('Regra eliminada!');
      await loadRegras();
    } catch (error) {
      toast.error('Erro ao eliminar regra');
    }
  };

  const addPodeFazer = () => {
    if (novoPodeFazer.trim()) {
      setFormData(prev => ({
        ...prev,
        pode_fazer: [...prev.pode_fazer, novoPodeFazer.trim()]
      }));
      setNovoPodeFazer('');
    }
  };

  const addNaoPodeFazer = () => {
    if (novoNaoPodeFazer.trim()) {
      setFormData(prev => ({
        ...prev,
        nao_pode_fazer: [...prev.nao_pode_fazer, novoNaoPodeFazer.trim()]
      }));
      setNovoNaoPodeFazer('');
    }
  };

  const removePodeFazer = (index) => {
    setFormData(prev => ({
      ...prev,
      pode_fazer: prev.pode_fazer.filter((_, i) => i !== index)
    }));
  };

  const removeNaoPodeFazer = (index) => {
    setFormData(prev => ({
      ...prev,
      nao_pode_fazer: prev.nao_pode_fazer.filter((_, i) => i !== index)
    }));
  };

  // Função para reordenar itens via drag and drop
  const handleDragEnd = (result, listType) => {
    if (!result.destination) return;
    
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    
    if (sourceIndex === destIndex) return;
    
    setFormData(prev => {
      const list = [...prev[listType]];
      const [removed] = list.splice(sourceIndex, 1);
      list.splice(destIndex, 0, removed);
      return { ...prev, [listType]: list };
    });
  };

  // Filtrar regras por pesquisa
  const regrasFiltradas = searchTerm
    ? regras.filter(r => 
        r.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.descricao?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.categoria?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.pode_fazer?.some(p => p.toLowerCase().includes(searchTerm.toLowerCase())) ||
        r.nao_pode_fazer?.some(n => n.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : null;

  const categoriasParaMostrar = searchTerm 
    ? { 'Resultados da pesquisa': regrasFiltradas }
    : regrasPorCategoria;

  if (loading) {
    return embedded ? (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    ) : (
      <GestorLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </GestorLayout>
    );
  }

  const content = (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-stone-900 flex items-center gap-3">
              <BookOpen className="h-8 w-8 text-primary" />
              Regras do Sistema
            </h1>
            <p className="text-stone-500 mt-1">
              Consulte e edite as regras de funcionamento da aplicação
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleResetRegras}
              className="rounded-full"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reiniciar Padrão
            </Button>
            <Button 
              onClick={() => openEditDialog()}
              className="rounded-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Regra
            </Button>
          </div>
        </div>

        {/* Barra de pesquisa */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
          <Input
            placeholder="Pesquisar regras..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-12 rounded-xl"
          />
          {searchTerm && (
            <button 
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 bg-primary/5 border-primary/20">
            <p className="text-sm text-stone-500">Total de Regras</p>
            <p className="text-2xl font-bold text-primary">{regras.length}</p>
          </Card>
          <Card className="p-4 bg-emerald-50 border-emerald-200">
            <p className="text-sm text-stone-500">Categorias</p>
            <p className="text-2xl font-bold text-emerald-600">{Object.keys(regrasPorCategoria).length}</p>
          </Card>
          <Card className="p-4 bg-green-50 border-green-200">
            <p className="text-sm text-stone-500">Total "Pode Fazer"</p>
            <p className="text-2xl font-bold text-green-600">
              {regras.reduce((acc, r) => acc + (r.pode_fazer?.length || 0), 0)}
            </p>
          </Card>
          <Card className="p-4 bg-red-50 border-red-200">
            <p className="text-sm text-stone-500">Total "Não Pode Fazer"</p>
            <p className="text-2xl font-bold text-red-600">
              {regras.reduce((acc, r) => acc + (r.nao_pode_fazer?.length || 0), 0)}
            </p>
          </Card>
        </div>

        {/* Lista de regras por categoria */}
        <div className="space-y-4">
          {Object.entries(categoriasParaMostrar).map(([categoria, regrasCategoria]) => (
            <Card key={categoria} className="overflow-hidden">
              {/* Header da categoria */}
              <button
                onClick={() => setCategoriaAberta(categoriaAberta === categoria ? null : categoria)}
                className="w-full p-4 flex items-center justify-between bg-stone-50 hover:bg-stone-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{CATEGORIA_ICONS[categoria] || '📖'}</span>
                  <div className="text-left">
                    <h3 className="font-semibold text-stone-900">{categoria}</h3>
                    <p className="text-sm text-stone-500">{regrasCategoria.length} regra(s)</p>
                  </div>
                </div>
                {categoriaAberta === categoria ? (
                  <ChevronUp className="h-5 w-5 text-stone-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-stone-400" />
                )}
              </button>

              {/* Conteúdo expandido */}
              {categoriaAberta === categoria && (
                <div className="divide-y divide-stone-100">
                  {regrasCategoria.map(regra => (
                    <div key={regra.id} className="p-4">
                      {/* Header da regra */}
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-stone-800">{regra.titulo}</h4>
                          <p className="text-sm text-stone-500 mt-1">{regra.descricao}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(regra)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(regra.id)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Listas pode/não pode */}
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Pode fazer */}
                        {regra.pode_fazer?.length > 0 && (
                          <div className="bg-green-50 rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span className="text-sm font-medium text-green-700">Pode fazer</span>
                            </div>
                            <ul className="space-y-1.5">
                              {regra.pode_fazer.map((item, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-sm text-green-800">
                                  <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Não pode fazer */}
                        {regra.nao_pode_fazer?.length > 0 && (
                          <div className="bg-red-50 rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <XCircle className="h-4 w-4 text-red-600" />
                              <span className="text-sm font-medium text-red-700">Não pode fazer</span>
                            </div>
                            <ul className="space-y-1.5">
                              {regra.nao_pode_fazer.map((item, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-sm text-red-800">
                                  <X className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>

        {/* Dialog de edição */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingRegra ? 'Editar Regra' : 'Nova Regra'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Categoria */}
              <div>
                <label className="text-sm font-medium text-stone-700">Categoria *</label>
                <Input
                  value={formData.categoria}
                  onChange={(e) => setFormData(prev => ({ ...prev, categoria: e.target.value }))}
                  placeholder="Ex: Produção, Operadores, etc."
                  className="mt-1"
                  list="categorias-list"
                />
                <datalist id="categorias-list">
                  {Object.keys(regrasPorCategoria).map(cat => (
                    <option key={cat} value={cat} />
                  ))}
                </datalist>
              </div>

              {/* Título */}
              <div>
                <label className="text-sm font-medium text-stone-700">Título *</label>
                <Input
                  value={formData.titulo}
                  onChange={(e) => setFormData(prev => ({ ...prev, titulo: e.target.value }))}
                  placeholder="Título da regra"
                  className="mt-1"
                />
              </div>

              {/* Descrição */}
              <div>
                <label className="text-sm font-medium text-stone-700">Descrição</label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                  placeholder="Descrição da regra"
                  className="mt-1 w-full px-3 py-2 rounded-md border border-stone-200 focus:outline-none focus:ring-2 focus:ring-primary"
                  rows={2}
                />
              </div>

              {/* Pode fazer */}
              <div>
                <label className="text-sm font-medium text-green-700 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Pode fazer
                </label>
                <div className="mt-2 space-y-2">
                  <DragDropContext onDragEnd={(result) => handleDragEnd(result, 'pode_fazer')}>
                    <Droppable droppableId="pode_fazer_list">
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                          {formData.pode_fazer.map((item, idx) => (
                            <Draggable key={`pode-${idx}`} draggableId={`pode-${idx}`} index={idx}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`flex items-center gap-2 bg-green-50 p-2 rounded-lg ${
                                    snapshot.isDragging ? 'shadow-lg ring-2 ring-green-300' : ''
                                  }`}
                                >
                                  <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing text-green-400 hover:text-green-600">
                                    <GripVertical className="h-4 w-4" />
                                  </div>
                                  <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                                  <span className="flex-1 text-sm">{item}</span>
                                  <button onClick={() => removePodeFazer(idx)} className="text-stone-400 hover:text-red-500">
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                  <div className="flex gap-2">
                    <Input
                      value={novoPodeFazer}
                      onChange={(e) => setNovoPodeFazer(e.target.value)}
                      placeholder="Adicionar item..."
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPodeFazer())}
                    />
                    <Button type="button" variant="outline" onClick={addPodeFazer}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Não pode fazer */}
              <div>
                <label className="text-sm font-medium text-red-700 flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  Não pode fazer
                </label>
                <div className="mt-2 space-y-2">
                  <DragDropContext onDragEnd={(result) => handleDragEnd(result, 'nao_pode_fazer')}>
                    <Droppable droppableId="nao_pode_fazer_list">
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                          {formData.nao_pode_fazer.map((item, idx) => (
                            <Draggable key={`naopode-${idx}`} draggableId={`naopode-${idx}`} index={idx}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`flex items-center gap-2 bg-red-50 p-2 rounded-lg ${
                                    snapshot.isDragging ? 'shadow-lg ring-2 ring-red-300' : ''
                                  }`}
                                >
                                  <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing text-red-400 hover:text-red-600">
                                    <GripVertical className="h-4 w-4" />
                                  </div>
                                  <X className="h-4 w-4 text-red-500 flex-shrink-0" />
                                  <span className="flex-1 text-sm">{item}</span>
                                  <button onClick={() => removeNaoPodeFazer(idx)} className="text-stone-400 hover:text-red-500">
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                  <div className="flex gap-2">
                    <Input
                      value={novoNaoPodeFazer}
                      onChange={(e) => setNovoNaoPodeFazer(e.target.value)}
                      placeholder="Adicionar item..."
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addNaoPodeFazer())}
                    />
                    <Button type="button" variant="outline" onClick={addNaoPodeFazer}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Ordem */}
              <div>
                <label className="text-sm font-medium text-stone-700">Ordem</label>
                <Input
                  type="number"
                  value={formData.ordem}
                  onChange={(e) => setFormData(prev => ({ ...prev, ordem: parseInt(e.target.value) || 0 }))}
                  className="mt-1 w-24"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave}>
                <Save className="h-4 w-4 mr-2" />
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
  );

  return embedded ? content : <GestorLayout>{content}</GestorLayout>;
}
